import { useAppStore } from "@src/AppStore";
import { apiDRSCostItemCreate } from "@src/apis/drs-cost-item-create";
import { apiDRSCostItemDelete } from "@src/apis/drs-cost-item-delete";
import { apiDRSLabourCost } from "@src/apis/drs-labour-cost";
import { apiDRSProfitabilityUpdate } from "@src/apis/drs-profitability-update";
import { DRSSectionName } from "@src/modules/DRS/DRSPage";
import { DRSProjectCostItem } from "@src/types/drs/DRS";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDRS } from "../../useDRS";

const ShiftProfitability: React.FC<{ drsPageType: string }> = ({ drsPageType }) => {
  const { drs, refresh, updateSectionViaPath } = useDRS();
  const { defaultRoleId } = useAppStore();

  // State variables
  const [projectRevenue, setProjectRevenue] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [isSavingRevenue, setIsSavingRevenue] = useState(false);
  const [isSavingComments, setIsSavingComments] = useState(false);
  const [costItems, setCostItems] = useState<DRSProjectCostItem[]>([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const [modalPrice, setModalPrice] = useState("");
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DRSProjectCostItem | null>(null);
  const [labourCost, setLabourCost] = useState<number>(0);
  const [isLoadingLabourCost, setIsLoadingLabourCost] = useState(false);

  // Spray Seal Material Rate Modal State
  const [showSpraySealRateModal, setShowSpraySealRateModal] = useState(false);
  const [selectedSpraySealMaterial, setSelectedSpraySealMaterial] = useState<any>(null);
  const [spraySealModalCost, setSpraySealModalCost] = useState("");
  const [isSavingSpraySealRate, setIsSavingSpraySealRate] = useState(false);



  // Fetch labour cost from API
  const fetchLabourCost = React.useCallback(async () => {
    if (!drs?.jobDetails?.id) return;

    // Get depotPayroll from the project depot
    const depotId = drs.jobDetails.project.depot.id;
    if (!depotId) {
      console.warn("No depot ID available from project for labour cost calculation");
      return;
    }

    setIsLoadingLabourCost(true);
    try {
      const response = await apiDRSLabourCost({
        drsSummaryId: drs.jobDetails.id.toString(),
        depotPayroll: depotId,
      });

      // Response structure: { data: { total_cost: "7848.00", breakdown: {...} }, message: "" }
      const totalCost = parseFloat(response.data.total_cost);
      setLabourCost(isNaN(totalCost) ? 0 : totalCost);

      console.log("Labour cost fetched:", totalCost);
    } catch (error) {
      console.error("Error fetching labour cost:", error);
      setLabourCost(0);
    } finally {
      setIsLoadingLabourCost(false);
    }
  }, [drs]);

  // Initialize state from DRS data
  useEffect(() => {
    if (drs) {
      // Initialize comments from DRS data
      if (drs.jobDetails.comments) {
        setComments(drs.jobDetails.comments);
      }

      // Initialize project revenue from DRS data
      if (drs.jobDetails.projectRevenue !== null && drs.jobDetails.projectRevenue !== undefined) {
        setProjectRevenue(drs.jobDetails.projectRevenue.toString());
      }

      // Initialize cost items from DRS data
      if (drs.projectCostItems) {
        setCostItems(drs.projectCostItems);
      }

      // Fetch labour cost when DRS loads
      fetchLabourCost();
    }
  }, [drs, fetchLabourCost]);

  if (!drs) {
    return <div>Loading...</div>;
  }

  // Utility function to safely format numbers
  const safeToFixed = (value: any, decimals: number = 2): string => {
    const num = Number(value);
    if (isNaN(num) || !isFinite(num)) {
      return (0).toFixed(decimals);
    }
    return num.toFixed(decimals);
  };

  // Get mobile assets from the API response - only include assets with actual or edit data
  const getMobileAssets = () => {
    if (!drs) return [];

    const assets = [];

    // Get asphalt assets - filter to only include assets with meaningful actual or edit data
    if (drs.mobileAssets?.boral?.asphalt?.assets) {
      const filteredAsphaltAssets = drs.mobileAssets.boral.asphalt.assets.filter((asset) => {
        // Check if actual has meaningful data (available and has mobileAsset)
        const hasActualData = asset.actual?.available && asset.actual?.mobileAsset;

        // Check if edit has meaningful data (available and has mobileAsset)
        const hasEditData = asset.edit?.available && asset.edit?.mobileAsset;

        // Only include assets that have actual or edit data (ignore plan-only assets)
        return hasActualData || hasEditData;
      });
      assets.push(...filteredAsphaltAssets);
    }

    // Get spray seal assets - filter to only include assets with meaningful actual or edit data
    if (drs.mobileAssets?.boral?.spraySeal?.assets) {
      const filteredSprayAssets = drs.mobileAssets.boral.spraySeal.assets.filter((asset) => {
        // Check if actual has meaningful data (available and has mobileAsset)
        const hasActualData = asset.actual?.available && asset.actual?.mobileAsset;

        // Check if edit has meaningful data (available and has mobileAsset)
        const hasEditData = asset.edit?.available && asset.edit?.mobileAsset;

        // Only include assets that have actual or edit data (ignore plan-only assets)
        return hasActualData || hasEditData;
      });
      assets.push(...filteredSprayAssets);
    }

    return assets;
  };

  const getAssetDescription = (asset: any) => {
    // Priority: edit -> actual (plan data ignored)
    return asset.edit?.mobileAsset?.id || asset.actual?.mobileAsset?.id || "Unknown Asset";
  };

  const getAssetRate = (asset: any) => {
    // Use internalHireRate from the root level first, then from edit/actual (plan data ignored)
    const rootRate = asset.internalHireRate;
    const editRate = asset.edit?.mobileAsset?.internalHireRate;
    const actualRate = asset.actual?.mobileAsset?.internalHireRate;

    if (rootRate !== null && rootRate !== undefined) {
      return `$${rootRate}`;
    } else if (editRate !== null && editRate !== undefined) {
      return `$${editRate}`;
    } else if (actualRate !== null && actualRate !== undefined) {
      return `$${actualRate}`;
    }
    return ""; // Don't show any value if null
  };

  const getAssetQuantity = (asset: any) => {
    // Priority: edit -> actual (plan data ignored)
    const hours = asset.edit?.hours ?? asset.actual?.hours;
    return hours !== null && hours !== undefined ? hours : "";
  };

  const getAssetTotal = (asset: any) => {
    const rate = asset.internalHireRate ?? asset.edit?.mobileAsset?.internalHireRate ?? asset.actual?.mobileAsset?.internalHireRate;
    const hours = asset.edit?.hours ?? asset.actual?.hours;

    if (rate !== null && rate !== undefined && hours !== null && hours !== undefined) {
      return rate * hours;
    }
    return 0;
  };

  const getTotalCosts = () => {
    try {
      // Get current mobile assets
      const currentMobileAssets = getMobileAssets();

      // Calculate total from mobile assets
      const mobileAssetsTotal = (currentMobileAssets || []).reduce((total, asset) => {
        const assetTotal = getAssetTotal(asset);
        return total + (isNaN(assetTotal) ? 0 : assetTotal);
      }, 0);

      // Calculate total from subcontractor items
      const subcontractorTotal = (subcontractorItems || []).reduce((total, subItem) => {
        const subTotal = getSubcontractorTotal(subItem);
        return total + (isNaN(subTotal) ? 0 : subTotal);
      }, 0);

      // Calculate total from materials
      const currentMaterialItems = getMaterialsData();
      const materialsTotal = (currentMaterialItems || []).reduce((total, materialItem) => {
        const materialTotal = getMaterialTotal(materialItem);
        return total + (isNaN(materialTotal) ? 0 : materialTotal);
      }, 0);

      // Calculate total from cartage costs
      const currentCartageCostItems = getCartageCostData();
      const cartageCostTotal = (currentCartageCostItems || []).reduce((total, cartageItem) => {
        const cartageTotal = getCartageCostTotal(cartageItem);
        return total + (isNaN(cartageTotal) ? 0 : cartageTotal);
      }, 0);

      // Calculate total from saved custom cost items
      const savedCostItemsTotal = (costItems || []).reduce((total, item) => {
        const itemPrice = item.itemPrice || 0;
        const itemPriceNum = Number(itemPrice);
        return total + (isNaN(itemPriceNum) ? 0 : itemPriceNum);
      }, 0);

      // Calculate total from labour cost
      const labourTotal = labourCost || 0;

      // Convert all values to numbers to prevent string concatenation
      const mobileAssetsNum = Number(mobileAssetsTotal);
      const subcontractorNum = Number(subcontractorTotal);
      const materialsNum = Number(materialsTotal);
      const cartageCostNum = Number(cartageCostTotal);
      const savedCostItemsNum = Number(savedCostItemsTotal);
      const labourNum = Number(labourTotal);

      // Return sum of all cost categories, ensure it's a valid number
      const total = mobileAssetsNum + subcontractorNum + materialsNum + cartageCostNum + savedCostItemsNum + labourNum;
      const result = isNaN(total) || !isFinite(total) ? 0 : total;
      return result;
    } catch (error) {
      console.error("Error in getTotalCosts:", error);
      return 0;
    }
  };

  const getSubtotal = () => {
    const total = getTotalCosts();
    // Ensure we return a number, not a string
    return Number(total);
  };

  const getOverheadAmount = () => {
    const subtotal = getSubtotal();
    const overheadPercentage = drs?.jobDetails?.project?.percentageOverhead || 0;
    return (subtotal * overheadPercentage) / 100;
  };

  const getTotalCostsWithOverhead = () => {
    const subtotal = getSubtotal();
    const overhead = getOverheadAmount();

    // Ensure both are numbers before addition
    const subtotalNum = Number(subtotal);
    const overheadNum = Number(overhead);
    const result = subtotalNum + overheadNum;

    // Safety check - if we get a bad result, return 0
    if (isNaN(result) || !isFinite(result)) {
      return 0;
    }

    return result;
  };

  const getShiftMargin = () => {
    const revenue = projectRevenue ? parseFloat(projectRevenue) : 0;
    const totalCosts = getTotalCostsWithOverhead();
    const margin = revenue - totalCosts;
    return isNaN(margin) ? 0 : margin;
  };

  const mobileAssets = getMobileAssets();

  // Get subcontractor data from the API response
  const getSubcontractorData = () => {
    if (!drs?.subcontractor) return [];

    const subcontractorItems = [];

    // Process Asphalt subcontractor data
    if (drs.subcontractor.asphalt) {
      const asphalt = drs.subcontractor.asphalt;

      // Profiling Assets - multiply unitRate * hours
      if (asphalt.profilingAssets) {
        asphalt.profilingAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data (edit or actual only, plan data ignored)
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Profiling",
              item: asset,
              category: "profiling",
            });
          }
        });
      }

      // General Assets - multiply unitRate * quantity
      if (asphalt.generalAssets) {
        asphalt.generalAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data (edit or actual only, plan data ignored)
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor General",
              item: asset,
              category: "general",
            });
          }
        });
      }

      // Delivery Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (asphalt.deliveryTransport) {
        asphalt.deliveryTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Transport Delivery",
              item: transport,
              category: "delivery",
            });
          }
        });
      }

      // Profiling Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (asphalt.profilingTransport) {
        asphalt.profilingTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Transport Profiling",
              item: transport,
              category: "profilingTransport",
            });
          }
        });
      }
    }

    // Process Spray Seal subcontractor data
    if (drs.subcontractor.spraySeal) {
      const spraySeal = drs.subcontractor.spraySeal;

      // General Assets - multiply unitRate * quantity (edit or actual only, plan data ignored)
      if (spraySeal.generalAssets) {
        spraySeal.generalAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Spray Seal General",
              item: asset,
              category: "general",
            });
          }
        });
      }

      // Delivery Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (spraySeal.deliveryTransport) {
        spraySeal.deliveryTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Spray Seal Transport Delivery",
              item: transport,
              category: "delivery",
            });
          }
        });
      }
    }

    return subcontractorItems;
  };

  const getSubcontractorDescription = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    // For transport items (delivery, profilingTransport), use supplier.name (edit or actual only, plan data ignored)
    if (category === "delivery" || category === "profilingTransport") {
      const supplier = item.edit?.supplier || item.actual?.supplier;
      return supplier?.name || "Unknown Transport Item";
    }

    // For profiling and general assets, use supplierAgreement fields (edit or actual only, plan data ignored)
    // Priority: edit -> actual
    const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;

    if (agreement) {
      // Concatenate cName with itemDescription when both are available
      const cName = agreement.cName || "";
      const itemDescription = agreement.itemDescription || "";

      if (cName && itemDescription) {
        return `${cName} - ${itemDescription}`;
      } else if (cName) {
        return cName;
      } else if (itemDescription) {
        return itemDescription;
      } else if (agreement.description) {
        return agreement.description;
      }
    }

    return "Unknown Item";
  };

  const getSubcontractorRate = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    let unitRate = null;

    if (category === "delivery" || category === "profilingTransport") {
      // For transport items, check supplier object first, then supplierAgreement
      const supplier = item.edit?.supplier || item.actual?.supplier;
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = supplier?.unitRate ?? agreement?.unitRate;
    } else {
      // For profiling and general items, use supplierAgreement
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = agreement?.unitRate;
    }

    return unitRate !== null && unitRate !== undefined ? `$${unitRate}` : "";
  };

  const getSubcontractorQuantity = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    // Priority: edit -> actual (plan data ignored)
    if (category === "profiling") {
      // For profiling assets, use hours
      const hours = item.edit?.hours ?? item.actual?.hours;
      return hours !== null && hours !== undefined ? hours : "";
    } else if (category === "general") {
      // For general assets, use quantity
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      return quantity !== null && quantity !== undefined ? quantity : "";
    } else {
      // For transport, use quantity if available
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      return quantity !== null && quantity !== undefined ? quantity : "";
    }
  };

  const getSubcontractorTotal = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    let unitRate = null;

    if (category === "delivery" || category === "profilingTransport") {
      // For transport items, check supplier object first, then supplierAgreement
      const supplier = item.edit?.supplier || item.actual?.supplier;
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = supplier?.unitRate ?? agreement?.unitRate;
    } else {
      // For profiling and general items, use supplierAgreement
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = agreement?.unitRate;
    }

    if (category === "profiling") {
      // Profiling: unitRate * hours
      const hours = item.edit?.hours ?? item.actual?.hours;
      if (unitRate !== null && unitRate !== undefined && hours !== null && hours !== undefined) {
        return unitRate * hours;
      }
    } else if (category === "general") {
      // General: unitRate * quantity
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      if (unitRate !== null && unitRate !== undefined && quantity !== null && quantity !== undefined) {
        return unitRate * quantity;
      }
    } else if (category === "delivery" || category === "profilingTransport") {
      // Transport: unitRate * quantity (if both are available)
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      if (unitRate !== null && unitRate !== undefined && quantity !== null && quantity !== undefined) {
        return unitRate * quantity;
      }
    }
    // Return 0 if no valid calculation can be made
    return 0;
  };

  const subcontractorItems = getSubcontractorData();

  // Get materials data from the API response
  const getMaterialsData = () => {
    if (!drs?.material) return [];

    const materialItems = [];

    // Process Asphalt materials (they have product with price field)
    if (drs.material.asphalt?.detail) {
      drs.material.asphalt.detail.forEach((material) => {
        // Priority: edit -> actual (ignore plan)
        // Check if edit has meaningful data
        const hasEditData = material.edit?.available && material.edit?.product?.id;

        // Check if actual has meaningful data
        const hasActualData = material.actual?.available && material.actual?.product?.id;

        // Only include materials that have edit or actual data (ignore plan-only materials)
        if (hasEditData || hasActualData) {
          materialItems.push({
            type: "Asphalt Material",
            material: material,
            category: "asphalt",
          });
        }
      });
    }

    // Process Spray Seal materials (they have aggregateSize and aggregateUsed)
    if (drs.material.spraySeal?.detail) {
      drs.material.spraySeal.detail.forEach((material, materialIndex) => {
        // Priority: edit -> actual (ignore plan)
        // Check if edit has meaningful data
        const hasEditData = material.edit?.available && (material.edit?.aggregateSize?.id || material.edit?.binderType?.id);
        
        // Check if actual has meaningful data
        const hasActualData = material.actual?.available && (material.actual?.aggregateSize?.id || material.actual?.binderType?.id);
        
        // Only include materials that have edit or actual data (ignore plan-only materials)
        if (hasEditData || hasActualData) {
          // Add binder type row if binder exists and has residualBinder
          const binderType = material.edit?.binderType || material.actual?.binderType;
          const residualBinder = material.edit?.residualBinder ?? material.actual?.residualBinder;

          if (binderType && residualBinder !== null && residualBinder !== undefined) {
            materialItems.push({
              type: 'Spray Seal Binder',
              material: material,
              category: 'spraySealBinder',
              materialIndex: materialIndex
            });
          }

          // Add aggregate row if aggregate exists and has aggregateUsed (keep existing functionality)
          const aggregateSize = material.edit?.aggregateSize || material.actual?.aggregateSize;
          const aggregateUsed = material.edit?.aggregateUsed ?? material.actual?.aggregateUsed;

          if (aggregateSize && aggregateUsed !== null && aggregateUsed !== undefined) {
            materialItems.push({
              type: 'Spray Seal Material',
              material: material,
              category: 'spraySeal',
              materialIndex: materialIndex
            });
          }
        }
      });
    }

    return materialItems;
  };

  const getMaterialDescription = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, show binder type description
      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;
      return binderType?.description || binderType?.id || 'Spray Binder Type';
    }
    
    if (category === 'spraySeal') {
      // For spray seal aggregate, show aggregate size description
      // Check if edit has meaningful aggregate data (id is not null), otherwise use actual
      const editAggregateSize = material.edit?.aggregateSize?.id ? material.edit.aggregateSize : null;
      const actualAggregateSize = material.actual?.aggregateSize?.id ? material.actual.aggregateSize : null;
      const aggregateSize = editAggregateSize || actualAggregateSize;
      
      if (aggregateSize) {
        return aggregateSize.description || aggregateSize.id || "Unknown Aggregate";
      }
      return "Unknown Aggregate";
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (id is not null)
    const editProduct = material.edit?.product;
    const actualProduct = material.actual?.product;
    
    const product = (editProduct?.id ? editProduct : actualProduct);

    if (product?.description) {
      return product.description;
    }
    
    if (product?.id) {
      return product.id;
    }

    return "Unknown Material";
  };

  const getMaterialRate = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, use binder type price (read-only from server)
      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;
      
      const price = binderType?.price;
      return price !== null && price !== undefined ? `$${price}` : '$0.00';
    }
    
    if (category === 'spraySeal') {
      // For spray seal aggregate, use materialRate from API (top-level field, not in plan/actual/edit)
      const materialRate = material.materialRate;
      return materialRate !== null && materialRate !== undefined ? materialRate.toString() : "";
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (price is not null)
    const editPrice = material.edit?.product?.price;
    const actualPrice = material.actual?.product?.price;
    
    const price = (editPrice !== null && editPrice !== undefined) ? editPrice : actualPrice;

    return price !== null && price !== undefined ? `$${price}` : "";
  };

  const getMaterialQuantity = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, use residualBinder
      const residualBinder = material.edit?.residualBinder ?? material.actual?.residualBinder;
      return residualBinder !== null && residualBinder !== undefined ? residualBinder : '';
    }

    if (category === 'spraySeal') {
      // For spray seal aggregate, use aggregateUsed
      const aggregateUsed = material.edit?.aggregateUsed ?? material.actual?.aggregateUsed;
      return aggregateUsed !== null && aggregateUsed !== undefined ? aggregateUsed : "";
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    const editTonnes = material.edit?.tonnes;
    const actualTonnes = material.actual?.tonnes;
    
    const tonnes = (editTonnes !== null && editTonnes !== undefined) ? editTonnes : actualTonnes;

    return tonnes !== null && tonnes !== undefined ? tonnes : "";
  };

  const getMaterialTotal = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, calculate from binder type price * residualBinder
      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;
      const rate = parseFloat(binderType?.price || '0');
      const residualBinder = parseFloat(material.edit?.residualBinder ?? material.actual?.residualBinder ?? '0');
      
      // Safety check for valid numbers
      if (isNaN(rate) || isNaN(residualBinder) || !isFinite(rate) || !isFinite(residualBinder)) {
        return 0;
      }
      
      const total = rate * residualBinder;;
      
      // Safety check for the result
      if (isNaN(total) || !isFinite(total)) {
        return 0;
      }
      
      return total;
    }
    
    if (category === 'spraySeal') {
      // For spray seal aggregate, calculate from materialRate (API field) * aggregateUsed
      const materialRate = material.materialRate;
      const rate = materialRate !== null && materialRate !== undefined ? parseFloat(materialRate.toString()) : 0;

      const aggregateUsed = parseFloat(material.edit?.aggregateUsed ?? material.actual?.aggregateUsed ?? "0");

      // Safety check for valid numbers
      if (isNaN(rate) || isNaN(aggregateUsed) || !isFinite(rate) || !isFinite(aggregateUsed)) {
        return 0;
      }

      const total = rate * aggregateUsed;

      // Safety check for the result
      if (isNaN(total) || !isFinite(total)) {
        return 0;
      }

      return total;
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (price is not null)
    const editPrice = material.edit?.product?.price;
    const actualPrice = material.actual?.product?.price;
    const price = (editPrice !== null && editPrice !== undefined) ? editPrice : actualPrice;
    
    // Check if edit has meaningful tonnes data
    const editTonnes = material.edit?.tonnes;
    const actualTonnes = material.actual?.tonnes;
    const tonnes = (editTonnes !== null && editTonnes !== undefined) ? editTonnes : actualTonnes;

    // If price is 0 or null, return 0
    if (price === 0 || price === null || price === undefined) {
      return 0;
    }

    if (tonnes !== null && tonnes !== undefined) {
      return price * tonnes;
    }

    return 0;
  };

  const materialItems = getMaterialsData();

  // Get cartage cost data from the API response
  const getCartageCostData = () => {
    if (!drs?.material) return [];

    // Type assertion to access cartageCostSummary (exists in API but not in type definition yet)
    const material = drs.material as any;
    if (!material.cartageCostSummary?.details) return [];

    // Return the cartage cost details
    return material.cartageCostSummary.details;
  };

  const getCartageCostDescription = (cartageItem: any) => {
    // Use plantId as the description
    return cartageItem.plantId ? `Cartage - ${cartageItem.plantId}` : "Cartage Cost";
  };

  const getCartageCostRate = (cartageItem: any) => {
    // Show base cost as the rate
    const baseCost = cartageItem.baseCost;
    return baseCost !== null && baseCost !== undefined ? `$${baseCost.toFixed(2)}` : "";
  };

  const getCartageCostQuantity = (cartageItem: any) => {
    // Show tonnes as quantity
    const tonnes = cartageItem.tonnes;
    return tonnes !== null && tonnes !== undefined ? tonnes : "";
  };

  const getCartageCostTotal = (cartageItem: any) => {
    // Use the totalCost from the API
    const totalCost = cartageItem.totalCost;
    return totalCost !== null && totalCost !== undefined ? totalCost : 0;
  };

  const cartageCostItems = getCartageCostData();

  // Cost Item Management Functions
  const addNewCostItemRow = () => {
    setShowAddItemModal(true);
    setModalDescription("");
    setModalPrice("");
  };

  const saveModalItem = async () => {
    if (!drs || !modalDescription.trim() || !modalPrice) return;

    setIsSavingModal(true);
    try {
      const response = await apiDRSCostItemCreate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        itemDescription: modalDescription.trim(),
        itemPrice: parseFloat(modalPrice),
      });

      console.log("API Response:", response);

      // Create a new cost item object using the real data from API response
      const newCostItem: DRSProjectCostItem = {
        id: response.data.costItem.id, // Use the real ID from API response
        drsSummaryId: response.data.costItem.drsSummaryId,
        itemDescription: response.data.costItem.itemDescription,
        itemPrice: parseFloat(response.data.costItem.itemPrice),
        isDeleted: response.data.costItem.isDeleted,
        dateCreated: response.data.costItem.dateCreated,
        dateModified: response.data.costItem.dateModified,
      };

      // Add the new cost item to saved items
      setCostItems((prev) => [...prev, newCostItem]);

      // Close modal and reset fields
      setShowAddItemModal(false);
      setModalDescription("");
      setModalPrice("");

      console.log("Cost item added successfully");
    } catch (error) {
      console.error("Error adding cost item:", error);
    } finally {
      setIsSavingModal(false);
    }
  };

  const closeModal = () => {
    setShowAddItemModal(false);
    setModalDescription("");
    setModalPrice("");
  };

  const handleDeleteClick = (costItem: DRSProjectCostItem) => {
    setItemToDelete(costItem);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    await deleteCostItem(itemToDelete);
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const deleteCostItem = async (costItem: DRSProjectCostItem) => {
    if (!drs) return;

    try {
      await apiDRSCostItemDelete({
        drsId: drs.jobDetails.id.toString(),
        costItemId: costItem.id.toString(),
        roleId: defaultRoleId,
      });

      // Remove the cost item from local state
      setCostItems((prev) => prev.filter((item) => item.id !== costItem.id));

      // Update profitability after deleting cost item (if revenue is available)
      if (projectRevenue) {
        await apiDRSProfitabilityUpdate({
          drsId: drs.jobDetails.id.toString(),
          roleId: defaultRoleId,
          projectRevenue: parseFloat(projectRevenue),
        });
      }

      console.log("Cost item deleted successfully");
    } catch (error) {
      console.error("Error deleting cost item:", error);
    }
  };

  const saveRevenue = async () => {
    if (!drs || projectRevenue === null || projectRevenue === undefined || projectRevenue === "") return;

    setIsSavingRevenue(true);
    try {
      await apiDRSProfitabilityUpdate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        projectRevenue: parseFloat(projectRevenue),
        comments: comments, // Always include current comments to prevent deletion
      });

      // Refresh the DRS data to get updated values
      await refresh();

      console.log("Revenue saved successfully");
    } catch (error) {
      console.error("Error saving revenue:", error);
    } finally {
      setIsSavingRevenue(false);
    }
  };

  const saveComments = async () => {
    if (!drs || !comments.trim()) return;

    setIsSavingComments(true);
    try {
      await apiDRSProfitabilityUpdate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        comments: comments,
        projectRevenue: projectRevenue ? parseFloat(projectRevenue) : undefined, // Always include current revenue to prevent deletion
      });

      // Refresh the DRS data to get updated values
      await refresh();

      console.log("Comments saved successfully");
    } catch (error) {
      console.error("Error saving comments:", error);
    } finally {
      setIsSavingComments(false);
    }
  };

  // Spray Seal Material Rate Functions
  const openSpraySealRateModal = (materialItem: any, materialIndex: number) => {
    setSelectedSpraySealMaterial({ ...materialItem, index: materialIndex });

    // Get existing rate from API field (materialRate is at top level, not in plan/actual/edit)
    const existingRate = materialItem.material.materialRate?.toString() || "";

    setSpraySealModalCost(existingRate);
    setShowSpraySealRateModal(true);
  };

  const closeSpraySealRateModal = () => {
    setShowSpraySealRateModal(false);
    setSelectedSpraySealMaterial(null);
    setSpraySealModalCost("");
  };

  const saveSpraySealRate = async () => {
    if (!selectedSpraySealMaterial || !spraySealModalCost || !drs) return;

    setIsSavingSpraySealRate(true);
    try {
      const materialRate = parseFloat(spraySealModalCost);
      
      // Find the material by ID instead of using index, since index might be wrong
      const materialId = selectedSpraySealMaterial.material.id;
      
      console.log("Debug - Looking for material with ID:", materialId);
      console.log("Debug - Current spraySeal detail:", drs.material.spraySeal.detail);

      // Clone the entire spray seal detail array and update the specific material's materialRate
      const updatedDetail = [...drs.material.spraySeal.detail];
      
      // Filter out any objects that only have materialRate (cleanup any previous duplicates)
      const cleanedDetail = updatedDetail.filter((item: any) => 
        item.id && item.materialSummaryId && (item.plan || item.actual || item.edit)
      );
      
      console.log("Debug - Cleaned detail array:", cleanedDetail);
      
      // Find and update the specific material by ID
      const materialIndexInArray = cleanedDetail.findIndex((item: any) => item.id === materialId);
      
      if (materialIndexInArray >= 0) {
        console.log("Debug - Found material at index:", materialIndexInArray);
        console.log("Debug - Material before update:", cleanedDetail[materialIndexInArray]);
        
        cleanedDetail[materialIndexInArray] = {
          ...cleanedDetail[materialIndexInArray],
          materialRate: materialRate,
        };
        
        console.log("Debug - Material after update:", cleanedDetail[materialIndexInArray]);
      } else {
        console.error("Debug - Material not found with ID:", materialId);
        throw new Error("Material not found");
      }

      const dataToSend = {
        summary: drs.material.spraySeal.summary,
        detail: cleanedDetail,
      };
      
      console.log("Debug - Data being sent to API:", dataToSend);

      // Use the same approach as MaterialSpraySealItems.tsx - replace entire spraySeal section
      await updateSectionViaPath({
        sectionName: DRSSectionName.Material,
        path: 'spraySeal',
        data: dataToSend,
      });

      // Update local state after successful API call
      refresh();

      console.log("Spray seal rate saved successfully:", {
        drsId: drs.jobDetails.id,
        materialId,
        materialRate,
        materialIndexInArray,
        updatedMaterial: cleanedDetail[materialIndexInArray],
      });

      // Close modal and reset
      closeSpraySealRateModal();
    } catch (error) {
      console.error("Error saving spray seal rate:", error);
      // Show error message to the user
      alert("Failed to save material cost. Please try again.");
    } finally {
      setIsSavingSpraySealRate(false);
    }
  };



  return (
    <Wrapper>
      <Header>
        <Title>Shift Profitability</Title>
        <ProjectInfo>
          <InfoBox>
            <Label>Project Revenue</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Revenue to Date</Label>
            <Value>(AIMS)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Quoted Shifts</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Quoted Margin</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
        </ProjectInfo>
      </Header>

      <Section>
        <RevenueHeader>
          <SectionTitle>Revenue</SectionTitle>
          <SaveButton onClick={saveRevenue} disabled={isSavingRevenue}>
            {isSavingRevenue ? "Saving..." : "Save Revenue"}
          </SaveButton>
        </RevenueHeader>
        <Table>
          <TableHeader>
            <tr>
              <HeaderCell>Value ($)</HeaderCell>
            </tr>
          </TableHeader>
          <tbody>
            <TableRow>
              <TableCell>
                <Input value={projectRevenue} onChange={(e) => setProjectRevenue(e.target.value)} placeholder="0.00" type="number" step="0.01" />
              </TableCell>
            </TableRow>
          </tbody>
        </Table>
      </Section>

      <Section>
        <SectionTitle $costs>Costs</SectionTitle>
        <CostsTable>
          <CostsHeader>
            <tr>
              <HeaderCell>Group</HeaderCell>
              <HeaderCell>Item</HeaderCell>
              <HeaderCell>Rate</HeaderCell>
              <HeaderCell>Qty</HeaderCell>
              <HeaderCell>Total</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </tr>
          </CostsHeader>
          <tbody>
            {mobileAssets.length > 0 ? (
              mobileAssets.map((asset, index) => (
                <TableRow key={asset.id || index}>
                  {index === 0 && <TableCell rowSpan={mobileAssets.length}>Assets</TableCell>}
                  <TableCell>{getAssetDescription(asset)}</TableCell>
                  <TableCell>{getAssetRate(asset)}</TableCell>
                  <TableCell>{getAssetQuantity(asset)}</TableCell>
                  <TableCell>{getAssetTotal(asset) > 0 ? `$${getAssetTotal(asset)}` : ""}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell>Assets</TableCell>
                <TableCell>No assets available</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Subcontractor Items */}
            {subcontractorItems.length > 0 ? (
              subcontractorItems.map((subItem, index) => (
                <TableRow key={`subcontractor-${subItem.type}-${index}`}>
                  {index === 0 && <TableCell rowSpan={subcontractorItems.length}>Subcontractor</TableCell>}
                  <TableCell>{getSubcontractorDescription(subItem)}</TableCell>
                  <TableCell>{getSubcontractorRate(subItem)}</TableCell>
                  <TableCell>{getSubcontractorQuantity(subItem)}</TableCell>
                  <TableCell>{getSubcontractorTotal(subItem) > 0 ? `$${getSubcontractorTotal(subItem)}` : ""}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell>Subcontractor</TableCell>
                <TableCell>No subcontractor items available</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Material Items */}
            {materialItems.length > 0 ? (
              materialItems.map((materialItem, index) => (
                <MaterialRow
                  key={`material-${materialItem.category}-${index}`}
                  materialItem={materialItem}
                  index={index}
                  totalItems={materialItems.length}
                  getMaterialDescription={getMaterialDescription}
                  getMaterialRate={getMaterialRate}
                  getMaterialQuantity={getMaterialQuantity}
                  getMaterialTotal={getMaterialTotal}
                  onOpenRateModal={openSpraySealRateModal}
                />
              ))
            ) : (
              <TableRow>
                <TableCell>Materials</TableCell>
                <TableCell>No materials available</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Cartage Cost Items */}
            {cartageCostItems.length > 0
              ? cartageCostItems.map((cartageItem, index) => (
                  <TableRow key={`cartage-${cartageItem.materialId}-${index}`}>
                    {index === 0 && <TableCell rowSpan={cartageCostItems.length}>Cartage Cost</TableCell>}
                    <TableCell>{getCartageCostDescription(cartageItem)}</TableCell>
                    <TableCell>{getCartageCostRate(cartageItem)}</TableCell>
                    <TableCell>{getCartageCostQuantity(cartageItem)}</TableCell>
                    <TableCell>{getCartageCostTotal(cartageItem) > 0 ? `$${safeToFixed(getCartageCostTotal(cartageItem), 2)}` : "$0.00"}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                ))
              : null}

            {/* Labour Cost */}
            <TableRow>
              <TableCell>Labour</TableCell>
              <TableCell>{isLoadingLabourCost ? "Loading labour cost..." : "Total Labour Cost"}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>{isLoadingLabourCost ? "" : labourCost > 0 ? `$${safeToFixed(labourCost, 2)}` : "$0.00"}</TableCell>
              <TableCell></TableCell>
            </TableRow>

            {/* Custom Cost Items - Saved Items */}
            {costItems.length > 0 &&
              costItems.map((item, index) => <CostItemRow key={item.id} item={item} index={index} isFirst={index === 0} totalItems={costItems.length + 1} onDelete={handleDeleteClick} />)}

            {/* Add New Cost Item Button Row */}
            {costItems.length > 0 && (
              <TableRow>
                <TableCell></TableCell>
                <TableCell colSpan={4}>
                  <AddNewItemButton onClick={addNewCostItemRow}>+ Add Cost Item</AddNewItemButton>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Show Add Button as First Row if No Items */}
            {costItems.length === 0 && (
              <TableRow>
                <TableCell>Custom Items</TableCell>
                <TableCell colSpan={4}>
                  <AddNewItemButton onClick={addNewCostItemRow}>+ Add Cost Item</AddNewItemButton>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Sub Total Row */}
            <TotalRow>
              <TableCell>
                <strong>Sub total</strong>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                <strong>${safeToFixed(getSubtotal())}</strong>
              </TableCell>
              <TableCell></TableCell>
            </TotalRow>

            {/* Overhead Row */}
            <OverheadRow>
              <TableCell>
                <strong>Overhead ({drs?.jobDetails?.project?.percentageOverhead || 0}%)</strong>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                <strong>${safeToFixed(getOverheadAmount())}</strong>
              </TableCell>
              <TableCell></TableCell>
            </OverheadRow>

            {/* Total Costs Row */}
            <FinalTotalRow>
              <TableCell>
                <strong>Total Costs</strong>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                <strong>${safeToFixed(getTotalCostsWithOverhead())}</strong>
              </TableCell>
              <TableCell></TableCell>
            </FinalTotalRow>
          </tbody>
        </CostsTable>
      </Section>

      <Footer>
        <FooterBox>
          <FooterLabel>Shift Margin (Revenue - Total Costs)</FooterLabel>
          <FooterInput value={`$${safeToFixed(getShiftMargin())}`} readOnly placeholder="$0" />
        </FooterBox>
        <CommentsBox>
          <CommentsHeader>
            <FooterLabel>Comments</FooterLabel>
            <SaveButton onClick={saveComments} disabled={isSavingComments}>
              {isSavingComments ? "Saving..." : "Save Comments"}
            </SaveButton>
          </CommentsHeader>
          <CommentsInput value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Add comments..." />
        </CommentsBox>
      </Footer>

      {/* Add Cost Item Modal */}
      {showAddItemModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Add Cost Item</ModalHeader>
            <ModalBody>
              <ModalField>
                <ModalLabel>Item Description</ModalLabel>
                <ModalInput type="text" value={modalDescription} onChange={(e) => setModalDescription(e.target.value)} placeholder="Enter item description..." />
              </ModalField>
              <ModalField>
                <ModalLabel>Total Price</ModalLabel>
                <ModalInput type="number" step="0.01" value={modalPrice} onChange={(e) => setModalPrice(e.target.value)} placeholder="0.00" />
              </ModalField>
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={closeModal} variant="cancel">
                Cancel
              </ModalButton>
              <ModalButton onClick={saveModalItem} disabled={isSavingModal || !modalDescription.trim() || !modalPrice} variant="save">
                {isSavingModal ? "Saving..." : "Save"}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>Confirm Delete</h3>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete the cost item:</p>
              <strong>"{itemToDelete.itemDescription}"</strong>
              <p>This action cannot be undone.</p>
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={cancelDelete} variant="cancel">
                Cancel
              </ModalButton>
              <DeleteButton onClick={confirmDelete}>Delete</DeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Spray Seal Material Rate Modal */}
      {showSpraySealRateModal && selectedSpraySealMaterial && (
        <ModalOverlay onClick={closeSpraySealRateModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Set Material Cost</ModalHeader>
            <ModalBody>
              <ModalField>
                <ModalLabel>Material</ModalLabel>
                <ModalReadOnlyText>{getMaterialDescription(selectedSpraySealMaterial)}</ModalReadOnlyText>
              </ModalField>
              <ModalField>
                <ModalLabel>Aggregate Used</ModalLabel>
                <ModalReadOnlyText>{getMaterialQuantity(selectedSpraySealMaterial)}</ModalReadOnlyText>
              </ModalField>
              <ModalField>
                <ModalLabel>Cost per Unit ($)</ModalLabel>
                <ModalInput type="number" step="0.01" value={spraySealModalCost} onChange={(e) => setSpraySealModalCost(e.target.value)} placeholder="0.00" />
              </ModalField>
              {spraySealModalCost && getMaterialQuantity(selectedSpraySealMaterial) && (
                <ModalField>
                  <ModalLabel>Total Cost</ModalLabel>
                  <ModalReadOnlyText style={{ fontWeight: 600, color: "#28a745" }}>
                    ${(parseFloat(spraySealModalCost) * parseFloat(getMaterialQuantity(selectedSpraySealMaterial).toString())).toFixed(2)}
                  </ModalReadOnlyText>
                </ModalField>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={closeSpraySealRateModal} variant="cancel">
                Cancel
              </ModalButton>
              <ModalButton onClick={saveSpraySealRate} disabled={isSavingSpraySealRate || !spraySealModalCost} variant="save">
                {isSavingSpraySealRate ? "Saving..." : "Save"}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

    </Wrapper>
  );
};

// Material Row Component - handles both asphalt and spray seal materials
const MaterialRow: React.FC<{
  materialItem: any;
  index: number;
  totalItems: number;
  getMaterialDescription: (item: any) => string;
  getMaterialRate: (item: any) => string;
  getMaterialQuantity: (item: any) => string | number;
  getMaterialTotal: (item: any) => number;
  onOpenRateModal: (item: any, index: number) => void;
}> = ({ materialItem, index, totalItems, getMaterialDescription, getMaterialRate, getMaterialQuantity, getMaterialTotal, onOpenRateModal }) => {
  const isSpraySeal = materialItem.category === "spraySeal";
  const rateValue = getMaterialRate(materialItem);

  return (
    <TableRow>
      {index === 0 && <TableCell rowSpan={totalItems}>Materials</TableCell>}
      <TableCell>{getMaterialDescription(materialItem)}</TableCell>
      <TableCell>
        {isSpraySeal ? <EditRateButton onClick={() => onOpenRateModal(materialItem, index)}>{rateValue ? `$${rateValue}` : "+ Add Cost"}</EditRateButton> : getMaterialRate(materialItem)}
      </TableCell>
      <TableCell>{getMaterialQuantity(materialItem)}</TableCell>
      <TableCell>{getMaterialTotal(materialItem) > 0 ? `$${getMaterialTotal(materialItem).toFixed(2)}` : "$0.00"}</TableCell>
      <TableCell></TableCell>
    </TableRow>
  );
};

// Cost Item Row Component
const CostItemRow: React.FC<{
  item: DRSProjectCostItem;
  index: number;
  isFirst: boolean;
  totalItems: number;
  onDelete: (item: DRSProjectCostItem) => void;
}> = ({ item, index, isFirst, totalItems, onDelete }) => {
  return (
    <TableRow>
      {isFirst && <TableCell rowSpan={totalItems}>Custom Items</TableCell>}
      <TableCell>{item.itemDescription}</TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell>{`$${Number(item.itemPrice || 0).toFixed(2)}`}</TableCell>
      <ActionCell>
        <ActionButtons>
          <DeleteActionButton onClick={() => onDelete(item)}>🗑</DeleteActionButton>
        </ActionButtons>
      </ActionCell>
    </TableRow>
  );
};

const Wrapper = styled.div`
  padding: 20px 0;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #4a90e2;
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: 600;
`;

const ProjectInfo = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const InfoBox = styled.div`
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 4px;
  padding: 8px 12px;
  min-width: 120px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
`;

const Value = styled.div`
  font-size: 14px;
  color: #333;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const RevenueHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.div<{ $costs?: boolean }>`
  background: ${(props) => (props.$costs ? "#f4a4a4" : "#a8d8a8")};
  color: ${(props) => (props.$costs ? "#8b0000" : "#2d5016")};
  padding: 8px 16px;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #ddd;
`;

const CostsTable = styled(Table)`
  margin-top: 0;
`;

const TableHeader = styled.thead`
  background: #999;
  color: white;
`;

const CostsHeader = styled(TableHeader)`
  background: #999;
`;

const HeaderCell = styled.th<{ width?: string }>`
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  border: 1px solid #ddd;
  width: ${(props) => props.width || "auto"};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9f9f9;
  }
`;

const TotalRow = styled.tr`
  background: #e0e0e0;
  border-top: 2px solid #ccc;
  font-weight: bold;

  &:nth-child(even) {
    background: #e0e0e0;
  }
`;

const OverheadRow = styled.tr`
  background: #f0f0f0;
  border-top: 1px solid #ccc;
  font-weight: bold;

  &:nth-child(even) {
    background: #f0f0f0;
  }
`;

const FinalTotalRow = styled.tr`
  background: #d0d0d0;
  border-top: 3px solid #aaa;
  font-weight: bold;

  &:nth-child(even) {
    background: #d0d0d0;
  }
`;

const TableCell = styled.td<{ rowSpan?: number; colSpan?: number }>`
  padding: 8px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
  row-span: ${(props) => props.rowSpan || 1};
  col-span: ${(props) => props.colSpan || 1};
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px;
  text-align: center;

  &:focus {
    outline: 1px solid #4a90e2;
    background: white;
  }
`;

const SaveButton = styled.button`
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const FooterBox = styled.div`
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 4px;
  padding: 12px;
  flex: 1;
`;

const FooterLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
`;

const FooterInput = styled.input`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  font-weight: ${(props) => (props.readOnly ? "600" : "normal")};
  background-color: ${(props) => (props.readOnly ? "#f5f5f5" : "white")};
  color: ${(props) => (props.readOnly ? "#333" : "inherit")};

  &:read-only {
    cursor: default;
  }
`;

const CommentsBox = styled.div`
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 4px;
  padding: 12px;
  flex: 1;
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CommentsInput = styled.textarea`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  min-height: 60px;
  resize: vertical;
`;

// Action cell and button styles for cost items
const ActionCell = styled(TableCell)`
  width: 80px;
  padding: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteActionButton = styled(ActionButton)`
  background: #dc3545;
  color: white;

  &:hover {
    background: #c82333;
  }
`;

const AddNewItemButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  width: 100%;

  &:hover {
    background: #218838;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  font-size: 18px;
  font-weight: 600;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalField = styled.div`
  margin-bottom: 20px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button<{ variant: "cancel" | "save" }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;

  ${(props) =>
    props.variant === "cancel"
      ? `
    background: white;
    color: #6c757d;
    border-color: #6c757d;
    
    &:hover {
      background: #f8f9fa;
    }
  `
      : `
    background: #28a745;
    color: white;
    border-color: #28a745;
    
    &:hover:not(:disabled) {
      background: #218838;
      border-color: #1e7e34;
    }
    
    &:disabled {
      background: #6c757d;
      border-color: #6c757d;
      cursor: not-allowed;
    }
  `}
`;

const DeleteButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-left: 10px;
  background-color: #dc3545;
  color: white;
  border: 1px solid #dc3545;

  &:hover {
    background-color: #c82333;
    border-color: #bd2130;
  }
`;

const EditRateButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background: #218838;
  }
`;

const ModalReadOnlyText = styled.div`
  padding: 10px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
`;

export default observer(ShiftProfitability);
